import type { TradeInput } from './schema';

export const insuranceFields = [
  { field: 'policyNumber', label: '保单号 / Policy No.', section: '保单信息', required: true },
  { field: 'policyIssueDate', label: '签发日期 / Issue Date', section: '保单信息', required: true, inputType: 'date' },
  { field: 'assured', label: '被保险人 / Assured', section: '保单信息', required: true, multiline: true },
  { field: 'invoiceNumber', label: '发票号 / Invoice No.', section: '保单信息', required: true },
  { field: 'currency', label: '币种 / Currency', section: '保单信息', required: true },
  { field: 'insuredAmount', label: '保险金额 / Amount Insured', section: '保单信息', required: true, inputType: 'decimal' },
  { field: 'insuredAmountWords', label: '大写金额原文 / Say', section: '保单信息' },
  { field: 'portOfLoading', label: '起运港 / From', section: '运输信息', required: true },
  { field: 'portOfDischarge', label: '目的港 / To', section: '运输信息', required: true },
  { field: 'vessel', label: '船名 / Vessel', section: '运输信息', required: true },
  { field: 'voyageNumber', label: '航次 / Voyage', section: '运输信息', required: true },
  { field: 'sailingDate', label: '开航日期 / Sailing Date', section: '运输信息', required: true, inputType: 'date' },
  { field: 'claimPayableAt', label: '赔款地点 / Claim Payable at', section: '运输信息', required: true },
  { field: 'insuranceCoverage', label: '承保险别 / Conditions & Coverage', section: '承保范围', required: true, multiline: true },
  { field: 'insuranceAdditionalRisks', label: '附加险 / Additional Risks', section: '承保范围', multiline: true },
  { field: 'insuranceOtherRisks', label: '其他附加险 / Other', section: '承保范围', multiline: true },
  { field: 'surveyAgent', label: '检验代理人 / Survey Agent', section: '条款与签章', multiline: true },
  { field: 'insuranceDeclaration', label: '保险条款声明 / Insurance Conditions & Declaration', section: '条款与签章', multiline: true },
  { field: 'applicantSignature', label: '投保人签章 / Applicant’s Signature & Stamp', section: '条款与签章', multiline: true },
  { field: 'insurerSignature', label: '保险公司签章 / Insurer’s Signature & Stamp', section: '条款与签章', multiline: true },
  { field: 'note', label: '备注 / Remarks', section: '条款与签章', multiline: true },
  { field: 'insuranceRemarksDate', label: '备注日期 / Date', section: '条款与签章', inputType: 'date' },
] as const satisfies readonly { field: keyof TradeInput; label: string; section: string; required?: boolean; inputType?: 'date' | 'decimal'; multiline?: boolean }[];

export type InsuranceRisk = Readonly<{ value: string; label: string; aliases: readonly string[] }>;
export const insuranceMainRisks: readonly InsuranceRisk[] = [
  { value: 'ICC (A)', label: 'ICC (A) / 一切险（All Risks）', aliases: ['ICC(A)', 'Institute Cargo Clauses (A)', 'ICC (A) 一切险'] },
  { value: 'ICC (B)', label: 'ICC (B) / 水渍险（WA）', aliases: ['ICC(B)', 'Institute Cargo Clauses (B)', 'ICC (B) 水渍险'] },
  { value: 'ICC (C)', label: 'ICC (C) / 平安险（FPA）', aliases: ['ICC(C)', 'Institute Cargo Clauses (C)', 'ICC (C) 平安险'] },
  { value: 'PICC All Risks', label: '中国海运货物保险条款（PICC）— 一切险', aliases: ['PICC 一切险', '中国海运货物保险条款 — 一切险'] },
  { value: 'PICC WA', label: '中国海运货物保险条款（PICC）— 水渍险', aliases: ['PICC 水渍险', '中国海运货物保险条款 — 水渍险'] },
  { value: 'PICC FPA', label: '中国海运货物保险条款（PICC）— 平安险', aliases: ['PICC 平安险', '中国海运货物保险条款 — 平安险'] },
];
export const insuranceAdditionalRiskOptions: readonly InsuranceRisk[] = [
  { value: 'War Risk', label: 'War Risk / 战争险', aliases: ['战争险', 'Institute War Clauses (Cargo)'] },
  { value: 'SRCC', label: 'SRCC / 罢工暴动险', aliases: ['罢工暴动险', '罢工险', 'Institute Strikes Clauses (Cargo)'] },
  { value: 'All Risks Extension', label: 'All Risks Extension / 扩展险', aliases: ['扩展险'] },
  { value: 'Theft, Pilferage & Non-Delivery', label: 'Theft, Pilferage & Non-Delivery / 偷窃提货不着险', aliases: ['偷窃提货不着险', 'TPND'] },
  { value: 'Fresh Water & Rain Damage', label: 'Fresh Water & Rain Damage / 淡水雨淋险', aliases: ['淡水雨淋险', 'Fresh Water / Rain Damage'] },
];

const normalize = (value: string) => value.normalize('NFKC').replace(/\s+/gu, '').toLowerCase();
export function insuranceRiskTokens(value: string | null | undefined): string[] {
  return (value ?? '').split(/[;；\n]+/u).map(token => token.trim()).filter(Boolean);
}
export function matchesInsuranceRisk(token: string, option: InsuranceRisk): boolean {
  return [option.value, option.label, ...option.aliases].some(alias => normalize(alias) === normalize(token));
}
export function insuranceRiskSelected(value: string | null | undefined, option: InsuranceRisk): boolean {
  return insuranceRiskTokens(value).some(token => matchesInsuranceRisk(token, option));
}
export function toggleInsuranceRisk(value: string | null | undefined, option: InsuranceRisk, checked: boolean): string | null {
  const tokens = insuranceRiskTokens(value).filter(token => !matchesInsuranceRisk(token, option));
  if (checked) tokens.push(option.value);
  return tokens.join('; ') || null;
}
